import { useParams } from "react-router";
import { put } from "../../common/api/apiShared";
import { StepModel } from "../../common/components/dotLegalStepper/DotLegalCoreStepper.types";
import { useOptimisticUpdate } from "../../common/hooks/useOptimisticUpdate";
import { useSystemSteps } from "../hooks/useSystemSteps";
import { SystemModel, SystemUpdateModel } from "./SystemEdit.types";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { EntityTemplate } from "../../entityTemplates/EntityTemplate.types.ts";
import { useValidateSystemStep } from "./SystemValidator.hooks.ts";

export function useSystemHook() {
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();
    const { id } = useParams<{ id: string }>();

    const queryKey = getSystemEditQueryKey(id);
    const { data: systemModel, isLoading, refetch: refetchSystem } = useDotLegalQueryWithKey<SystemModel>(queryKey, "/system/" + id);

    const entityTemplateUrl = `/entity-templates/${systemModel?.entityTemplateId}`;
    const entityTemplateQuery = useDotLegalQuery<EntityTemplate>(entityTemplateUrl, {
        enabled: !!systemModel,
    });

    const { validateStep } = useValidateSystemStep(id ?? "");

    function getSteps() {
        const steps: Array<StepModel> = [];

        if (entityTemplateQuery.data) {
            entityTemplateQuery.data.steps
                .sort((a, b) => a.order - b.order)
                .forEach((step) => {
                    steps.push(new StepModel(step.name, step.order, () => validateStep(step, systemModel!)));
                });
        }

        return steps;
    }

    const steps = useSystemSteps(systemModel ?? new SystemModel(), entityTemplateQuery.data, getSteps());

    function changeStep(step: number) {
        steps.changeStep(step);
    }

    async function onChange(changeFunc: (system: SystemModel) => void) {
        const tempSystem = { ...systemModel! };
        changeFunc(tempSystem);
        await save(tempSystem);
    }

    function onCustomFieldChange(changeFunc: (system: SystemModel) => void) {
        const tempSystem = { ...systemModel! };
        changeFunc(tempSystem);
        queryClient.setQueryData(queryKey, tempSystem);
    }

    async function save(system: SystemModel) {
        await putOnQueueAndSetQueryData(system, queryKey, updateSystemAPI, system);
    }

    return {
        isLoading: isLoading || entityTemplateQuery.isLoading,
        system: systemModel!,
        systemTemplate: entityTemplateQuery.data,
        steps: steps.getSteps(),
        activeStep: steps.getActiveStep(),
        changeStep,
        validationErrors: steps.validationErrors,
        onChange,
        onCustomFieldChange,
        systemId: id,
        refetchSystem,
    };

    async function updateSystemAPI(viewModel: SystemModel) {
        return await put<SystemModel>("/system/" + id, convertToUpdateModel(viewModel));
    }

    function convertToUpdateModel(viewModel: SystemModel): SystemUpdateModel {
        return {
            name: viewModel.name,
            description: viewModel.description,
            id: viewModel.id,
            businessAreas: viewModel.businessAreas,
            distributionArea: viewModel.distributionArea,
            deletionPeriodId: viewModel.deletionPeriodId,
            securityMeasures: viewModel.securityMeasures,
            dataStorageLocations: viewModel.dataStorageLocations,
            supportAccessLocations: viewModel.supportAccessLocations,
            responsibleId: viewModel.responsibleId,
            deletionPeriodReason: viewModel.deletionPeriodReason,
            dataTypes: viewModel.dataTypes,
            entityType: viewModel.entityType,
            storageType: viewModel.storageType,
            policies: viewModel.policies,
            status: viewModel.status,
            certificationIds: viewModel.certifications,
            isCriticalForOperation: viewModel.isCriticalForOperation,
            network: viewModel.network,
            disableSystemRelations: viewModel.disableSystemRelations,
            usesAI: viewModel.usesAI,
            companyUsesAIService: viewModel.companyUsesAIService,
            aIUsageDescription: viewModel.aiUsageDescription,
            isUseOfAIConsideredHighRisk: viewModel.isUseOfAIConsideredHighRisk,
            isDataUsedForTraningOfAIModel: viewModel.isDataUsedForTraningOfAIModel,
            operationCriticalityLevel: viewModel.operationCriticalityLevel,
            customFieldValues: viewModel.customFieldValues,
            visibleToAllUsers: viewModel.visibleToAllUsers,
            allGroupEntityAccess: viewModel.allGroupEntityAccess,
            accessBusinessAreas: viewModel.accessBusinessAreas,
            additionalAccessUsers: viewModel.additionalAccessUsers,
            excludedUsers: viewModel.excludedUsers,
            groupEntities: viewModel.groupEntities,
        };
    }
}

export function getSystemEditQueryKey(systemId: string | undefined) {
    return "system" + systemId;
}
